/**
 * Statistiche
 * ===========
 * Pagina admin che mostra le statistiche di vendita aggregate.
 *  - Filtri preset: Giorno (oggi) | Settimana (da Lun) | Mese (dal 1°) | Custom (range date)
 *  - KPI: Incasso totale, # ordini, Scontrino medio, Coperti totali
 *  - Tabelle: Top 10 piatti redditivi, Bottom 10, Vendite per cameriere
 *  - Grafico barre: incasso per giorno (mostra solo se period != day)
 *  - Export CSV con tutti i piatti del periodo
 *
 * Endpoint: GET /api/stats/summary?period=day|week|month|custom&start=YYYY-MM-DD&end=YYYY-MM-DD
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Users, Euro, Receipt, Calendar, Download, RefreshCw, BarChart3, Sandwich, Clock, Tag as TagIcon, ChefHat, ArrowUp, ArrowDown, Minus, ShieldAlert, AlertTriangle } from "lucide-react";

const PRESETS = [
    { key: "day", label: "Giorno" },
    { key: "week", label: "Settimana" },
    { key: "month", label: "Mese" },
    { key: "custom", label: "Custom" },
];

const TIME_PRESETS = [
    { key: "all", label: "Tutto il giorno", hs: null, he: null },
    { key: "lunch", label: "Pranzo (12-15)", hs: 12, he: 15 },
    { key: "dinner", label: "Cena (19-23)", hs: 19, he: 23 },
    { key: "custom", label: "Custom", hs: null, he: null },
];

const DEPT_OPTIONS = [
    { key: "", label: "Tutti i reparti", color: "text-elegant" },
    { key: "cucina", label: "Cucina", color: "text-amber-400" },
    { key: "bar", label: "Bar", color: "text-cyan-400" },
    { key: "pizzeria", label: "Pizzeria", color: "text-red-400" },
];

const DEPT_COLOR = (d) => ({
    cucina: "text-amber-400 bg-amber-900/30 border-amber-500/40",
    bar: "text-cyan-400 bg-cyan-900/30 border-cyan-500/40",
    pizzeria: "text-red-400 bg-red-900/30 border-red-500/40",
}[(d || "").toLowerCase()] || "text-elegant bg-deep border-[#1f2c44]");

const fmtEur = (n) => `€ ${(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Statistiche = () => {
    const [period, setPeriod] = useState("day");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [timePreset, setTimePreset] = useState("all");
    const [hourStart, setHourStart] = useState(12);
    const [hourEnd, setHourEnd] = useState(15);
    const [deptFilter, setDeptFilter] = useState("");      // "" = tutti
    const [tagFilter, setTagFilter] = useState("");        // "" = nessuno
    const [availableTags, setAvailableTags] = useState([]); // dai preset + in_use
    const [data, setData] = useState(null);
    const [voidedData, setVoidedData] = useState(null);
    const [voidedDays, setVoidedDays] = useState(30);
    const [loading, setLoading] = useState(false);

    // Carica tag disponibili (preset + in uso)
    useEffect(() => {
        api.get("/menu/item-tags").then(({ data }) => {
            const merged = [...new Set([...(data.presets || []), ...(data.in_use || [])])];
            setAvailableTags(merged);
        }).catch(() => {});
    }, []);

    const loadVoided = async (d) => {
        try {
            const { data } = await api.get(`/stats/voided-tables?days=${d}`);
            setVoidedData(data);
        } catch (e) { /* silent */ }
    };

    useEffect(() => { loadVoided(voidedDays); /* eslint-disable-next-line */ }, [voidedDays]);

    const load = async () => {
        setLoading(true);
        try {
            let url = `/stats/summary?period=${period}`;
            if (period === "custom") {
                if (!customStart || !customEnd) {
                    toast.info("Seleziona inizio e fine periodo");
                    setLoading(false);
                    return;
                }
                url += `&start=${customStart}&end=${customEnd}`;
            }
            // Filtro orario
            if (timePreset !== "all") {
                const preset = TIME_PRESETS.find(p => p.key === timePreset);
                const hs = preset?.hs !== null && preset?.hs !== undefined ? preset.hs : hourStart;
                const he = preset?.he !== null && preset?.he !== undefined ? preset.he : hourEnd;
                if (hs != null && he != null && hs <= he) {
                    url += `&hour_start=${hs}&hour_end=${he}`;
                }
            }
            // Filtri reparto/tag
            if (deptFilter) url += `&department=${encodeURIComponent(deptFilter)}`;
            if (tagFilter) url += `&tag=${encodeURIComponent(tagFilter)}`;
            const { data } = await api.get(url);
            setData(data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Auto-load eccetto quando l'utente è in custom senza date impostate
        if (period === "custom" && (!customStart || !customEnd)) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, timePreset, deptFilter, tagFilter]);

    // Auto-reload quando cambiano gli orari custom (debounce 400ms)
    useEffect(() => {
        if (timePreset !== "custom") return;
        const t = setTimeout(() => {
            if (period !== "custom") load();
        }, 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hourStart, hourEnd]);

    const maxRevDay = useMemo(() => {
        if (!data?.by_day?.length) return 1;
        return Math.max(...data.by_day.map(d => d.revenue), 1);
    }, [data]);

    const exportCsv = () => {
        if (!data?.all_items?.length) {
            toast.info("Nessun dato da esportare");
            return;
        }
        const rows = [
            ["Periodo", data.period.label],
            ["Inizio", data.period.start],
            ["Fine", data.period.end],
            [],
            ["KPI"],
            ["Incasso totale", fmtEur(data.kpi.revenue_total)],
            ["Numero ordini", data.kpi.orders_count],
            ["Scontrino medio", fmtEur(data.kpi.avg_ticket)],
            ["Coperti totali", data.kpi.total_covers],
            [],
            ["VENDITE PER PIATTO"],
            ["Nome", "Quantità", "Incasso (€)"],
            ...data.all_items.map(i => [i.name, i.qty, i.revenue.toFixed(2)]),
            [],
            ["VENDITE PER CAMERIERE"],
            ["Cameriere", "Ordini", "Articoli", "Incasso (€)", "Coperti"],
            ...data.by_waiter.map(w => [w.waiter_name, w.orders, w.qty, w.revenue.toFixed(2), w.covers]),
        ];
        const csv = rows.map(r => r.map(c => {
            const s = String(c == null ? "" : c);
            return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(";")).join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `statistiche_${data.period.start}_${data.period.end}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV esportato");
    };

    return (
        <div className="space-y-6" data-testid="statistiche-page">
            {/* HEADER */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-bold flex items-center gap-2">
                        <BarChart3 className="w-7 h-7 text-brand" /> Statistiche
                    </h1>
                    <p className="text-xs text-elegant mt-1">Vendite, piatti più richiesti, performance camerieri</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={load}
                        className="h-10 px-3 border border-[#1f2c44] hover:border-brand rounded-md flex items-center gap-1 text-xs uppercase tracking-wider"
                        data-testid="stats-refresh"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
                    </button>
                    <button
                        onClick={exportCsv}
                        disabled={!data}
                        className="h-10 px-3 bg-brand text-deep font-bold rounded-md flex items-center gap-1 text-xs uppercase tracking-wider disabled:opacity-40"
                        data-testid="stats-export-csv"
                    >
                        <Download className="w-4 h-4" /> CSV
                    </button>
                </div>
            </div>

            {/* FILTRI */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                <div className="flex gap-2 flex-wrap items-center">
                    {PRESETS.map(p => (
                        <button
                            key={p.key}
                            onClick={() => setPeriod(p.key)}
                            data-testid={`stats-period-${p.key}`}
                            className={`h-10 px-4 rounded-md text-xs uppercase tracking-wider font-bold border-2 ${
                                period === p.key
                                    ? "bg-brand text-deep border-brand"
                                    : "border-[#1f2c44] text-elegant hover:border-brand"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                    {period === "custom" && (
                        <div className="flex items-center gap-2 ml-2">
                            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                                data-testid="stats-custom-start"
                                className="h-10 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-xs"/>
                            <span className="text-elegant text-xs">→</span>
                            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                                data-testid="stats-custom-end"
                                className="h-10 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-xs"/>
                            <button onClick={load}
                                data-testid="stats-custom-apply"
                                className="h-10 px-3 bg-brand text-deep font-bold rounded-md text-xs uppercase">
                                Applica
                            </button>
                        </div>
                    )}
                    {data && (
                        <div className="ml-auto flex items-center gap-2 text-elegant text-xs">
                            <Calendar className="w-4 h-4" />
                            <span className="font-mono">{data.period.label}</span>
                        </div>
                    )}
                </div>

                {/* Filtro fascia oraria */}
                <div className="flex gap-2 flex-wrap items-center mt-3 pt-3 border-t border-[#1f2c44]">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-elegant flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Orario:
                    </span>
                    {TIME_PRESETS.map(t => (
                        <button
                            key={t.key}
                            onClick={() => setTimePreset(t.key)}
                            data-testid={`stats-time-${t.key}`}
                            className={`h-9 px-3 rounded-md text-[11px] uppercase tracking-wider font-bold border-2 ${
                                timePreset === t.key
                                    ? "bg-amber-500 text-deep border-amber-500"
                                    : "border-[#1f2c44] text-elegant hover:border-amber-500"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                    {timePreset === "custom" && (
                        <div className="flex items-center gap-1 ml-1">
                            <input type="number" min="0" max="23" value={hourStart}
                                onChange={e => setHourStart(parseInt(e.target.value || "0", 10))}
                                data-testid="stats-hour-start"
                                className="h-9 w-14 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-xs font-mono text-center"/>
                            <span className="text-elegant text-xs">→</span>
                            <input type="number" min="0" max="23" value={hourEnd}
                                onChange={e => setHourEnd(parseInt(e.target.value || "23", 10))}
                                data-testid="stats-hour-end"
                                className="h-9 w-14 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-xs font-mono text-center"/>
                            <span className="text-elegant text-[10px]">h</span>
                        </div>
                    )}
                </div>

                {/* Filtri reparto + tag */}
                <div className="flex gap-2 flex-wrap items-center mt-3 pt-3 border-t border-[#1f2c44]">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-elegant flex items-center gap-1">
                        <ChefHat className="w-3 h-3" /> Reparto:
                    </span>
                    {DEPT_OPTIONS.map((d) => (
                        <button
                            key={d.key || "all"}
                            onClick={() => setDeptFilter(d.key)}
                            data-testid={`stats-dept-${d.key || "all"}`}
                            className={`h-9 px-3 rounded-md text-[11px] uppercase tracking-wider font-bold border-2 ${
                                deptFilter === d.key
                                    ? `bg-deep ${d.color} border-current`
                                    : "border-[#1f2c44] text-elegant hover:border-brand/60"
                            }`}
                        >
                            {d.label}
                        </button>
                    ))}

                    <span className="text-[10px] uppercase tracking-[0.2em] text-elegant flex items-center gap-1 ml-3">
                        <TagIcon className="w-3 h-3" /> Tag:
                    </span>
                    <select
                        value={tagFilter}
                        onChange={(e) => setTagFilter(e.target.value)}
                        data-testid="stats-tag-filter"
                        className="h-9 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-xs"
                    >
                        <option value="">Tutti</option>
                        {availableTags.map((t) => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                    {(deptFilter || tagFilter) && (
                        <button onClick={() => { setDeptFilter(""); setTagFilter(""); }}
                            data-testid="stats-clear-filters"
                            className="h-9 px-3 text-[10px] uppercase tracking-wider text-red-300 hover:text-red-200 border border-red-500/40 rounded">
                            Reset filtri
                        </button>
                    )}
                </div>
            </div>

            {!data && !loading && (
                <div className="text-center text-elegant py-12">Premi <strong>Aggiorna</strong> per caricare i dati.</div>
            )}
            {loading && <div className="text-center text-elegant py-12">Caricamento...</div>}

            {data && !loading && (
                <>
                    {/* KPI */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stats-kpi">
                        <KpiCard icon={Euro} label="Incasso" value={fmtEur(data.kpi.revenue_total)} accent="text-emerald-400"
                            compare={data.comparison ? { ...data.comparison.revenue, label: data.comparison.label, shortLabel: data.comparison.label.split(" ").slice(0, 3).join(" ") } : null} />
                        <KpiCard icon={Receipt} label="Ordini" value={data.kpi.orders_count} accent="text-cyan-400"
                            compare={data.comparison ? { ...data.comparison.orders, label: data.comparison.label, shortLabel: data.comparison.label.split(" ").slice(0, 3).join(" ") } : null} />
                        <KpiCard icon={TrendingUp} label="Scontrino medio" value={fmtEur(data.kpi.avg_ticket)} accent="text-amber-400" />
                        <KpiCard icon={Users} label="Coperti" value={data.kpi.total_covers} accent="text-purple-400"
                            compare={data.comparison ? { ...data.comparison.covers, label: data.comparison.label, shortLabel: data.comparison.label.split(" ").slice(0, 3).join(" ") } : null} />
                    </div>

                    {/* Filter summary banner */}
                    {data.filter_summary && (
                        <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4 flex flex-wrap items-center gap-4" data-testid="stats-filter-summary">
                            <div className="flex items-center gap-2 text-amber-300">
                                <Sandwich className="w-5 h-5" />
                                <span className="font-display text-2xl font-bold">{data.filter_summary.total_qty}</span>
                                <span className="text-sm uppercase tracking-wider">
                                    {data.filter_summary.tag ? `× ${data.filter_summary.tag}` :
                                        data.filter_summary.department ? (
                                            data.filter_summary.department === "pizzeria" ? "pizze"
                                            : data.filter_summary.department === "bar" ? "consumazioni bar"
                                            : "piatti cucina"
                                        ) : "piatti"}
                                </span>
                            </div>
                            <div className="h-8 w-px bg-amber-500/30"></div>
                            <div className="text-xs text-elegant">
                                <span className="font-bold text-emerald-400">{fmtEur(data.filter_summary.total_revenue)}</span> incassati ·{" "}
                                <span className="font-bold text-warm">{data.filter_summary.unique_items}</span> piatti distinti
                            </div>
                            {data.comparison?.qty_in_filter && data.comparison.qty_in_filter.previous > 0 && (
                                <>
                                    <div className="h-8 w-px bg-amber-500/30"></div>
                                    <div className={`text-xs flex items-center gap-1 font-bold ${
                                        data.comparison.qty_in_filter.delta > 0 ? "text-emerald-400" : data.comparison.qty_in_filter.delta < 0 ? "text-red-400" : "text-elegant"
                                    }`}>
                                        {data.comparison.qty_in_filter.delta > 0 ? <ArrowUp className="w-3 h-3" /> : data.comparison.qty_in_filter.delta < 0 ? <ArrowDown className="w-3 h-3" /> : null}
                                        <span>
                                            {data.comparison.qty_in_filter.delta > 0 ? "+" : ""}{data.comparison.qty_in_filter.delta}
                                            ({data.comparison.qty_in_filter.delta_pct != null ? `${data.comparison.qty_in_filter.delta_pct > 0 ? "+" : ""}${data.comparison.qty_in_filter.delta_pct}%` : ""})
                                        </span>
                                        <span className="font-normal opacity-70">vs precedente</span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Per reparto */}
                    {data.by_department?.length > 0 && (
                        <div className="bg-surface border border-[#1f2c44] rounded-lg p-4" data-testid="stats-by-department">
                            <h2 className="text-xs uppercase tracking-[0.2em] text-elegant mb-3 flex items-center gap-1">
                                <ChefHat className="w-3 h-3" /> Per reparto
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {data.by_department.map((d) => {
                                    const totalRev = data.by_department.reduce((s, x) => s + x.revenue, 0);
                                    const pct = totalRev > 0 ? Math.round((d.revenue / totalRev) * 100) : 0;
                                    return (
                                        <div key={d.department}
                                            data-testid={`stats-dept-card-${d.department}`}
                                            className={`rounded-md border p-3 ${DEPT_COLOR(d.department)}`}
                                        >
                                            <div className="text-[10px] uppercase tracking-wider opacity-80">{d.department}</div>
                                            <div className="font-display text-2xl font-bold mt-1">{fmtEur(d.revenue)}</div>
                                            <div className="text-xs mt-2 flex items-center justify-between opacity-90">
                                                <span>{d.qty} piatti</span>
                                                <span className="font-mono">{pct}%</span>
                                            </div>
                                            {/* Barra % */}
                                            <div className="mt-2 h-1.5 bg-black/30 rounded-full overflow-hidden">
                                                <div className="h-full bg-current rounded-full" style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* By Day (solo se period != day) */}
                    {period !== "day" && data.by_day.length > 1 && (
                        <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                            <h2 className="text-xs uppercase tracking-[0.2em] text-elegant mb-3 flex items-center gap-1">
                                <BarChart3 className="w-3 h-3" /> Incasso per giorno
                            </h2>
                            <div className="flex items-end gap-1 h-32" data-testid="stats-by-day">
                                {data.by_day.map(d => (
                                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                                        <div className="text-[10px] text-warm font-mono">{fmtEur(d.revenue).replace("€", "")}</div>
                                        <div className="w-full bg-brand/80 rounded-t hover:bg-brand transition"
                                            style={{ height: `${Math.max(4, (d.revenue / maxRevDay) * 100)}%` }}
                                            title={`${d.date}: ${fmtEur(d.revenue)} (${d.orders} ordini)`} />
                                        <div className="text-[10px] text-elegant truncate w-full text-center">{d.date.slice(5)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Top + Worst piatti */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <DishTable
                            title="Top 10 piatti redditivi"
                            icon={TrendingUp}
                            iconColor="text-emerald-400"
                            items={data.top_items}
                            testid="stats-top-items"
                        />
                        <DishTable
                            title="Bottom 10 piatti meno redditivi"
                            icon={TrendingDown}
                            iconColor="text-red-400"
                            items={data.worst_items}
                            testid="stats-worst-items"
                            emptyMsg="Nessun piatto con incasso basso"
                        />
                    </div>

                    {/* Per cameriere */}
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                        <h2 className="text-xs uppercase tracking-[0.2em] text-elegant mb-3 flex items-center gap-1">
                            <Users className="w-3 h-3" /> Vendite per cameriere
                        </h2>                        {data.by_waiter.length === 0 ? (
                            <div className="text-center text-elegant py-4 text-xs">Nessun dato</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs" data-testid="stats-by-waiter">
                                    <thead>
                                        <tr className="text-elegant uppercase tracking-wider text-[10px] border-b border-[#1f2c44]">
                                            <th className="text-left py-2 px-2">Cameriere</th>
                                            <th className="text-right py-2 px-2">Ordini</th>
                                            <th className="text-right py-2 px-2">Articoli</th>
                                            <th className="text-right py-2 px-2">Coperti</th>
                                            <th className="text-right py-2 px-2">Incasso</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.by_waiter.map(w => (
                                            <tr key={w.waiter_id} className="border-b border-[#1f2c44]/40 hover:bg-deep/40">
                                                <td className="py-2 px-2 text-warm">{w.waiter_name}</td>
                                                <td className="py-2 px-2 text-right font-mono">{w.orders}</td>
                                                <td className="py-2 px-2 text-right font-mono">{w.qty}</td>
                                                <td className="py-2 px-2 text-right font-mono">{w.covers}</td>
                                                <td className="py-2 px-2 text-right font-mono text-emerald-400 font-bold">{fmtEur(w.revenue)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Tavoli liberati (audit) */}
                    {voidedData && (
                        <div className="bg-red-950/10 border border-red-700/30 rounded-lg p-4" data-testid="stats-voided">
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                <h2 className="text-xs uppercase tracking-[0.2em] text-red-300 flex items-center gap-1">
                                    <ShieldAlert className="w-3 h-3" /> Tavoli liberati (audit)
                                </h2>
                                <div className="flex items-center gap-1">
                                    {[7, 30, 90].map((d) => (
                                        <button key={d} onClick={() => setVoidedDays(d)}
                                            data-testid={`voided-days-${d}`}
                                            className={`h-7 px-2 rounded text-[10px] uppercase tracking-wider font-bold border ${
                                                voidedDays === d ? "border-red-400 bg-red-900/40 text-red-200" : "border-[#1f2c44] text-elegant hover:border-red-500/60"
                                            }`}>
                                            {d}gg
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {voidedData.kpi.total_events === 0 ? (
                                <div className="text-center text-elegant py-6 text-xs">
                                    Nessun tavolo liberato negli ultimi {voidedData.period_days} giorni 🎉
                                </div>
                            ) : (
                                <>
                                    {/* KPI */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                                        <div className="bg-deep border border-[#1f2c44] rounded p-2">
                                            <div className="text-[10px] uppercase text-elegant">Eventi</div>
                                            <div className="font-display text-xl font-bold text-red-300">{voidedData.kpi.total_events}</div>
                                        </div>
                                        <div className="bg-deep border border-[#1f2c44] rounded p-2">
                                            <div className="text-[10px] uppercase text-elegant">Importo perso</div>
                                            <div className="font-display text-xl font-bold text-red-400">{fmtEur(voidedData.kpi.total_amount_lost)}</div>
                                        </div>
                                        <div className="bg-deep border border-[#1f2c44] rounded p-2">
                                            <div className="text-[10px] uppercase text-elegant">Con conto</div>
                                            <div className="font-display text-xl font-bold text-amber-300">{voidedData.kpi.with_amount_count}</div>
                                        </div>
                                        <div className="bg-deep border border-[#1f2c44] rounded p-2">
                                            <div className="text-[10px] uppercase text-elegant">A 0€</div>
                                            <div className="font-display text-xl font-bold text-emerald-300">{voidedData.kpi.zero_amount_count}</div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                        {/* Per operatore */}
                                        <div>
                                            <h3 className="text-[10px] uppercase tracking-wider text-elegant mb-2">Per operatore</h3>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-xs" data-testid="voided-by-operator">
                                                    <thead>
                                                        <tr className="text-elegant text-[10px] uppercase border-b border-[#1f2c44]">
                                                            <th className="text-left py-1 px-2">Operatore</th>
                                                            <th className="text-right py-1 px-2">Eventi</th>
                                                            <th className="text-right py-1 px-2">Importo</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {voidedData.by_operator.slice(0, 10).map((o) => (
                                                            <tr key={o.operator_name} className="border-b border-[#1f2c44]/40">
                                                                <td className="py-1.5 px-2 text-warm">
                                                                    <div>{o.operator_name}</div>
                                                                    {o.role && o.role !== "—" && <div className="text-[9px] text-elegant uppercase tracking-wider">{o.role}</div>}
                                                                </td>
                                                                <td className="py-1.5 px-2 text-right font-mono">{o.count}</td>
                                                                <td className="py-1.5 px-2 text-right font-mono text-red-400 font-bold">{fmtEur(o.amount)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* Top motivi */}
                                        <div>
                                            <h3 className="text-[10px] uppercase tracking-wider text-elegant mb-2">Motivi più frequenti</h3>
                                            <div className="space-y-1" data-testid="voided-top-reasons">
                                                {voidedData.top_reasons.slice(0, 8).map((r) => {
                                                    const total = voidedData.kpi.total_events || 1;
                                                    const pct = Math.round((r.count / total) * 100);
                                                    return (
                                                        <div key={r.reason} className="text-xs">
                                                            <div className="flex justify-between mb-0.5">
                                                                <span className="text-warm truncate max-w-[70%]">{r.reason}</span>
                                                                <span className="font-mono text-elegant">{r.count} · {pct}%</span>
                                                            </div>
                                                            <div className="h-1 bg-deep rounded-full overflow-hidden">
                                                                <div className="h-full bg-red-500/60 rounded-full" style={{ width: `${pct}%` }} />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Eventi recenti */}
                                    {voidedData.events.length > 0 && (
                                        <div className="mt-4">
                                            <h3 className="text-[10px] uppercase tracking-wider text-elegant mb-2">Eventi recenti (ultimi 20)</h3>
                                            <div className="overflow-x-auto max-h-80 overflow-y-auto">
                                                <table className="w-full text-xs" data-testid="voided-events">
                                                    <thead className="sticky top-0 bg-surface">
                                                        <tr className="text-elegant text-[10px] uppercase border-b border-[#1f2c44]">
                                                            <th className="text-left py-1 px-2">Data</th>
                                                            <th className="text-left py-1 px-2">Tavolo</th>
                                                            <th className="text-left py-1 px-2">Operatore</th>
                                                            <th className="text-left py-1 px-2">Motivo</th>
                                                            <th className="text-right py-1 px-2">Importo</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {voidedData.events.slice(0, 20).map((e, idx) => {
                                                            const date = e.timestamp ? new Date(e.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
                                                            return (
                                                                <tr key={idx} className="border-b border-[#1f2c44]/40 hover:bg-deep/40">
                                                                    <td className="py-1.5 px-2 text-elegant font-mono text-[10px]">{date}</td>
                                                                    <td className="py-1.5 px-2 text-warm font-bold">Tavolo {e.table_number ?? "?"}</td>
                                                                    <td className="py-1.5 px-2 text-warm">
                                                                        <div>{e.operator_name}</div>
                                                                        {e.by_role && e.by_role !== "—" && <div className="text-[9px] text-elegant uppercase">{e.by_role}</div>}
                                                                    </td>
                                                                    <td className="py-1.5 px-2 text-elegant text-[11px]">{e.reason || "—"}</td>
                                                                    <td className={`py-1.5 px-2 text-right font-mono font-bold ${e.voided_total_amount > 0 ? "text-red-400" : "text-elegant"}`}>
                                                                        {fmtEur(e.voided_total_amount)}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* Coperti per servizio (pranzo / cena / altro) */}
                    <CoversReport />
                </>
            )}
        </div>
    );
};

const CoversReport = () => {
    const [period, setPeriod] = useState("month");
    const [granularity, setGranularity] = useState("day");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            let url = `/stats/covers?period=${period}&granularity=${granularity}`;
            if (period === "custom") {
                if (!customStart || !customEnd) { setLoading(false); return; }
                url += `&start=${customStart}&end=${customEnd}`;
            }
            const { data } = await api.get(url);
            setData(data);
        } catch (e) { toast.error(formatError(e)); }
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, granularity, customStart, customEnd]);

    const totals = data?.totals;
    const rows = data?.rows || [];
    const maxBarValue = useMemo(() => {
        return rows.reduce((m, r) => Math.max(m, r.pranzo.total + r.cena.total + r.altro.total), 0) || 1;
    }, [rows]);

    return (
        <div className="bg-surface border border-[#1f2c44] rounded-lg p-4" data-testid="stats-covers">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h2 className="text-xs uppercase tracking-[0.2em] text-brand flex items-center gap-1">
                    <Users className="w-3 h-3" /> Coperti per servizio
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Periodo */}
                    <div className="flex items-center gap-1">
                        {[
                            { k: "week", l: "Settimana" },
                            { k: "month", l: "Mese" },
                            { k: "year", l: "Anno" },
                            { k: "custom", l: "Custom" },
                        ].map((p) => (
                            <button key={p.k} onClick={() => setPeriod(p.k)}
                                data-testid={`covers-period-${p.k}`}
                                className={`h-7 px-2 rounded text-[10px] uppercase tracking-wider font-bold border ${
                                    period === p.k ? "border-brand bg-brand/20 text-brand" : "border-[#1f2c44] text-elegant hover:border-brand/60"
                                }`}>
                                {p.l}
                            </button>
                        ))}
                    </div>
                    {period === "custom" && (
                        <div className="flex items-center gap-1">
                            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                                data-testid="covers-custom-start"
                                className="h-7 px-2 bg-deep border border-[#1f2c44] rounded text-[10px] text-warm" />
                            <span className="text-[10px] text-elegant">→</span>
                            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                                data-testid="covers-custom-end"
                                className="h-7 px-2 bg-deep border border-[#1f2c44] rounded text-[10px] text-warm" />
                        </div>
                    )}
                    {/* Granularity */}
                    <div className="flex items-center gap-1 border-l border-[#1f2c44] pl-2">
                        {[
                            { k: "day", l: "Giorno" },
                            { k: "month", l: "Mese" },
                        ].map((g) => (
                            <button key={g.k} onClick={() => setGranularity(g.k)}
                                data-testid={`covers-granularity-${g.k}`}
                                className={`h-7 px-2 rounded text-[10px] uppercase tracking-wider font-bold border ${
                                    granularity === g.k ? "border-brand bg-brand/20 text-brand" : "border-[#1f2c44] text-elegant hover:border-brand/60"
                                }`}>
                                {g.l}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {loading && <div className="text-center text-elegant py-4 text-xs">Caricamento…</div>}

            {data && !loading && (
                <>
                    <div className="text-[11px] text-elegant mb-3">{data.period.label}</div>

                    {/* KPI grandi */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                        <div className="bg-amber-900/15 border border-amber-500/40 rounded p-3" data-testid="covers-kpi-pranzo">
                            <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">☀️ Pranzo</div>
                            <div className="font-display text-2xl font-bold text-amber-200">{totals.pranzo.total}</div>
                            <div className="text-[10px] text-amber-300/80 mt-1">{totals.pranzo.ordini} ordini · media {totals.pranzo.avg_per_order}/tavolo</div>
                        </div>
                        <div className="bg-indigo-900/15 border border-indigo-500/40 rounded p-3" data-testid="covers-kpi-cena">
                            <div className="text-[10px] uppercase tracking-wider text-indigo-300 mb-1">🌙 Cena</div>
                            <div className="font-display text-2xl font-bold text-indigo-200">{totals.cena.total}</div>
                            <div className="text-[10px] text-indigo-300/80 mt-1">{totals.cena.ordini} ordini · media {totals.cena.avg_per_order}/tavolo</div>
                        </div>
                        <div className="bg-slate-900/30 border border-slate-500/40 rounded p-3" data-testid="covers-kpi-altro">
                            <div className="text-[10px] uppercase tracking-wider text-slate-300 mb-1">Altro (fuori orario)</div>
                            <div className="font-display text-2xl font-bold text-slate-200">{totals.altro.total}</div>
                            <div className="text-[10px] text-slate-400 mt-1">{totals.altro.ordini} ordini · media {totals.altro.avg_per_order}/tavolo</div>
                        </div>
                        <div className="bg-brand/10 border border-brand/40 rounded p-3" data-testid="covers-kpi-total">
                            <div className="text-[10px] uppercase tracking-wider text-brand mb-1">Totale coperti</div>
                            <div className="font-display text-2xl font-bold text-brand">{totals.grand_total}</div>
                            <div className="text-[10px] text-brand/80 mt-1">{totals.grand_total_orders} ordini totali</div>
                        </div>
                    </div>

                    {/* Tabella + grafico orizzontale stacked */}
                    {rows.length === 0 ? (
                        <div className="text-center text-elegant py-6 text-xs">Nessun coperto registrato nel periodo</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid="covers-table">
                                <thead>
                                    <tr className="text-elegant uppercase tracking-wider text-[10px] border-b border-[#1f2c44]">
                                        <th className="text-left py-2 px-2">{granularity === "day" ? "Giorno" : "Mese"}</th>
                                        <th className="text-right py-2 px-2 text-amber-300">Pranzo</th>
                                        <th className="text-right py-2 px-2 text-indigo-300">Cena</th>
                                        <th className="text-right py-2 px-2 text-slate-300">Altro</th>
                                        <th className="text-right py-2 px-2 text-brand">Totale</th>
                                        <th className="text-left py-2 px-2 w-1/3">Distribuzione</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.key} className="border-b border-[#1f2c44]/40 hover:bg-deep/40">
                                            <td className="py-2 px-2 text-warm font-mono">{r.label}</td>
                                            <td className="py-2 px-2 text-right font-mono text-amber-300">
                                                {r.pranzo.total}
                                                {r.pranzo.bambini > 0 && <span className="text-[9px] opacity-60 ml-1">({r.pranzo.adulti}A+{r.pranzo.bambini}B)</span>}
                                            </td>
                                            <td className="py-2 px-2 text-right font-mono text-indigo-300">
                                                {r.cena.total}
                                                {r.cena.bambini > 0 && <span className="text-[9px] opacity-60 ml-1">({r.cena.adulti}A+{r.cena.bambini}B)</span>}
                                            </td>
                                            <td className="py-2 px-2 text-right font-mono text-slate-300">{r.altro.total}</td>
                                            <td className="py-2 px-2 text-right font-mono font-bold text-brand">{r.total}</td>
                                            <td className="py-2 px-2">
                                                <div className="flex h-3 rounded overflow-hidden border border-[#1f2c44]" style={{ width: `${Math.max(20, (r.total / maxBarValue) * 100)}%`, minWidth: "60px" }}>
                                                    {r.pranzo.total > 0 && (
                                                        <div className="bg-amber-500/80" style={{ flex: r.pranzo.total }} title={`Pranzo ${r.pranzo.total}`} />
                                                    )}
                                                    {r.cena.total > 0 && (
                                                        <div className="bg-indigo-500/80" style={{ flex: r.cena.total }} title={`Cena ${r.cena.total}`} />
                                                    )}
                                                    {r.altro.total > 0 && (
                                                        <div className="bg-slate-500/80" style={{ flex: r.altro.total }} title={`Altro ${r.altro.total}`} />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="mt-3 text-[10px] text-elegant flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500/80 rounded-sm"></span> Pranzo 11:00-15:30</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-indigo-500/80 rounded-sm"></span> Cena 18:00-23:59</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-slate-500/80 rounded-sm"></span> Altro orario</span>
                    </div>
                </>
            )}
        </div>
    );
};

const KpiCard = ({ icon: Icon, label, value, accent, compare }) => {
    const pct = compare?.delta_pct;
    const positive = pct != null && pct > 0;
    const negative = pct != null && pct < 0;
    return (
        <div className="bg-surface border border-[#1f2c44] rounded-lg p-3 flex items-start gap-2">
            <Icon className={`w-5 h-5 ${accent} flex-shrink-0`} />
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-elegant truncate">{label}</div>
                <div className={`font-display text-xl font-bold ${accent} mt-0.5`}>{value}</div>
                {compare && (
                    <div
                        className={`text-[10px] mt-1 flex items-center gap-1 font-bold ${
                            positive ? "text-emerald-400" : negative ? "text-red-400" : "text-elegant"
                        }`}
                        title={`Confronto: ${compare.label || "periodo precedente"}`}
                    >
                        {positive && <ArrowUp className="w-2.5 h-2.5" />}
                        {negative && <ArrowDown className="w-2.5 h-2.5" />}
                        {pct == null && <Minus className="w-2.5 h-2.5" />}
                        <span>
                            {pct != null ? `${pct > 0 ? "+" : ""}${pct}%` : "—"}
                        </span>
                        <span className="text-elegant font-normal opacity-70">vs {compare.shortLabel}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const DishTable = ({ title, icon: Icon, iconColor, items, testid, emptyMsg = "Nessun dato" }) => (
    <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
        <h2 className="text-xs uppercase tracking-[0.2em] text-elegant mb-3 flex items-center gap-1">
            <Icon className={`w-3 h-3 ${iconColor}`} /> {title}
        </h2>
        {items.length === 0 ? (
            <div className="text-center text-elegant py-4 text-xs">{emptyMsg}</div>
        ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid={testid}>
                    <thead>
                        <tr className="text-elegant uppercase tracking-wider text-[10px] border-b border-[#1f2c44]">
                            <th className="text-left py-2 px-2"><Sandwich className="w-3 h-3 inline" /> Piatto</th>
                            <th className="text-right py-2 px-2">Q.tà</th>
                            <th className="text-right py-2 px-2">Incasso</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it, idx) => (
                            <tr key={`${it.name}-${idx}`} className="border-b border-[#1f2c44]/40 hover:bg-deep/40">
                                <td className="py-2 px-2 text-warm">
                                    <div className="flex items-start gap-1 flex-wrap">
                                        <span>{it.name}</span>
                                        {it.department && (
                                            <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${DEPT_COLOR(it.department)} border`}>
                                                {it.department}
                                            </span>
                                        )}
                                        {it.tags?.slice(0, 3).map((t) => (
                                            <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-purple-900/30 border border-purple-500/30 text-purple-300">
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="py-2 px-2 text-right font-mono">{it.qty}</td>
                                <td className={`py-2 px-2 text-right font-mono font-bold ${iconColor}`}>{fmtEur(it.revenue)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
);

export default Statistiche;
